import versionConfig from './negotiationProcessVersions.json';

const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const TIME_WINDOW_PATTERN = /^(\d{2}):(\d{2})-(\d{2}):(\d{2})$/;
const TRANSACTION_PAGE_SIZE = 100;

export const NEGOTIATION_PROCESS_NAME = versionConfig.processName;
export const LATEST_NEGOTIATION_PROCESS_VERSION = versionConfig.latestProcessVersion;
export const NEGOTIATION_SUPPORTED_PROCESS_VERSIONS = versionConfig.supportedProcessVersions;
export const NEGOTIATION_RELEASE_3_COMPATIBLE_PROCESS_VERSIONS =
  versionConfig.release3CompatibleProcessVersions;
export const NEGOTIATION_CANCELLATION_PROCESS_VERSIONS = versionConfig.cancellationProcessVersions;
export const NEGOTIATION_COMPLETION_PROCESS_VERSIONS = versionConfig.completionProcessVersions;
export const NEGOTIATION_LISTING_TYPES = versionConfig.listingTypes;
export const NEGOTIATION_ARCHIVE_BLOCKING_STATES = versionConfig.archiveBlockingStates;
export const NEGOTIATION_CANCELLATION_TRANSITIONS = versionConfig.cancellationTransitions;
export const NEGOTIATION_COMPLETION_TRANSITIONS = versionConfig.completionTransitions;

export const isNegotiationRelease3CompatibleProcessVersion = (processName, processVersion) =>
  processName === NEGOTIATION_PROCESS_NAME &&
  NEGOTIATION_RELEASE_3_COMPATIBLE_PROCESS_VERSIONS.includes(Number(processVersion));

const normalizeTransactionState = state =>
  typeof state === 'string' ? state.replace(/^state\//, '') : null;

export const isNegotiationArchiveBlockingTransaction = transaction => {
  const attributes = transaction?.attributes || {};
  return (
    isNegotiationRelease3CompatibleProcessVersion(
      attributes.processName,
      attributes.processVersion
    ) && NEGOTIATION_ARCHIVE_BLOCKING_STATES.includes(normalizeTransactionState(attributes.state))
  );
};

export const queryNegotiationArchiveBlockingTransactions = async (sdk, listingId) => {
  const transactions = [];
  let page = 1;
  let totalPages = 1;

  do {
    const response = await sdk.transactions.query({
      only: 'order',
      listingId,
      processNames: [NEGOTIATION_PROCESS_NAME],
      page,
      perPage: TRANSACTION_PAGE_SIZE,
      'fields.transaction': ['state', 'processName', 'processVersion'],
    });
    transactions.push(...(response?.data?.data || []));
    totalPages = response?.data?.meta?.totalPages || 1;
    page += 1;
  } while (page <= totalPages);

  return transactions.some(isNegotiationArchiveBlockingTransaction);
};

export const isNegotiationCancellationProcessVersion = (processName, processVersion) =>
  processName === NEGOTIATION_PROCESS_NAME &&
  NEGOTIATION_CANCELLATION_PROCESS_VERSIONS.includes(Number(processVersion));

export const isNegotiationCancellationTransition = (processName, processVersion, transitionName) =>
  isNegotiationCancellationProcessVersion(processName, processVersion) &&
  NEGOTIATION_CANCELLATION_TRANSITIONS.includes(transitionName);

export const isNegotiationCompletionProcessVersion = (processName, processVersion) =>
  processName === NEGOTIATION_PROCESS_NAME &&
  NEGOTIATION_COMPLETION_PROCESS_VERSIONS.includes(Number(processVersion));

export const isNegotiationCompletionTransition = (processName, processVersion, transitionName) =>
  isNegotiationCompletionProcessVersion(processName, processVersion) &&
  NEGOTIATION_COMPLETION_TRANSITIONS.includes(transitionName);

export const isNegotiationProtectedTransition = (processName, processVersion, transitionName) =>
  isNegotiationCancellationTransition(processName, processVersion, transitionName) ||
  isNegotiationCompletionTransition(processName, processVersion, transitionName);

const validTimePart = (hour, minute) =>
  Number(hour) >= 0 && Number(hour) <= 23 && Number(minute) >= 0 && Number(minute) <= 59;

export const getNegotiationServiceSchedule = protectedData => {
  const serviceDate = protectedData?.offeredServiceDate;
  const timeWindow = protectedData?.offeredStartTimeWindow;
  const match = typeof timeWindow === 'string' ? timeWindow.match(TIME_WINDOW_PATTERN) : null;
  if (typeof serviceDate !== 'string' || !DATE_PATTERN.test(serviceDate) || !match) return null;

  const parsedDate = new Date(`${serviceDate}T12:00:00.000Z`);
  if (
    !Number.isFinite(parsedDate.getTime()) ||
    parsedDate.toISOString().slice(0, 10) !== serviceDate
  ) {
    return null;
  }

  const [, startHour, startMinute, endHour, endMinute] = match;
  const startsAt = Number(startHour) * 60 + Number(startMinute);
  const endsAt = Number(endHour) * 60 + Number(endMinute);
  if (
    !validTimePart(startHour, startMinute) ||
    !validTimePart(endHour, endMinute) ||
    startsAt >= endsAt
  ) {
    return null;
  }
  return { serviceDate, timeWindow };
};

export const getNegotiationServiceStart = protectedData => {
  const schedule = getNegotiationServiceSchedule(protectedData);
  if (!schedule) return null;
  const [startTime] = schedule.timeWindow.split('-');
  // Puerto Rico stays on AST (UTC-04:00) throughout the year.
  const serviceStart = new Date(`${schedule.serviceDate}T${startTime}:00-04:00`);
  return Number.isFinite(serviceStart.getTime()) ? serviceStart : null;
};

export const isNegotiationPendingOfferSelectable = ({ transaction, now = new Date() }) => {
  const listing = transaction?.listing;
  const attributes = transaction?.attributes || {};
  const listingType = listing?.attributes?.publicData?.listingType;
  const listingIsOpen = listing?.attributes?.state === 'published';
  const serviceStart = getNegotiationServiceStart(attributes.protectedData);

  return (
    isNegotiationRelease3CompatibleProcessVersion(
      attributes.processName,
      attributes.processVersion
    ) &&
    NEGOTIATION_LISTING_TYPES.includes(listingType) &&
    listingIsOpen &&
    !!serviceStart &&
    now.getTime() < serviceStart.getTime()
  );
};

export const calendarDateKey = (date, timeZone = 'America/Puerto_Rico') => {
  const parsedDate = date instanceof Date ? date : new Date(date);
  if (!Number.isFinite(parsedDate.getTime())) return null;
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(parsedDate);
  const part = type => parts.find(item => item.type === type)?.value;
  return `${part('year')}-${part('month')}-${part('day')}`;
};

export const isBeforeNegotiationServiceDay = (
  now,
  protectedData,
  timeZone = 'America/Puerto_Rico'
) => {
  const schedule = getNegotiationServiceSchedule(protectedData);
  const currentDate = calendarDateKey(now, timeZone);
  return !!schedule && !!currentDate && currentDate < schedule.serviceDate;
};
